import { useState } from 'react';
import { api, queryString } from '../lib/api.ts';
import { useAsync } from '../lib/useAsync.ts';
import { useAuth } from '../lib/auth.tsx';
import { EmptyState, ErrorState, Loading, PageHeader } from '../components/ui.tsx';
import { VideoDetailModal } from '../components/VideoDetailModal.tsx';
import { formatDateTime, formatNumber } from '../lib/format.ts';
import { integrationNotConfiguredNotice } from '../lib/integrations.ts';
import type { NewsItem, NewsRelevance, NewsSourceType, VideoItem } from '../../../shared/types.ts';

const RELEVANCE_LABEL: Record<NewsRelevance, string> = {
  alta: 'Alta',
  media: 'Média',
  baixa: 'Baixa',
};

const RELEVANCE_CLASS: Record<NewsRelevance, string> = {
  alta: 'badge-status-success',
  media: 'badge-status-warning',
  baixa: 'badge-status-neutral',
};

const SOURCE_TYPE_LABEL: Record<NewsSourceType, string> = {
  tier1: 'Tier 1',
  reddit: 'Reddit',
  geral: 'Geral',
};

const TABS = ['Notícias', 'Vídeos'] as const;
type Tab = (typeof TABS)[number];

export function NewsPage() {
  const [tab, setTab] = useState<Tab>('Notícias');

  return (
    <>
      <PageHeader
        title="Radar Evanescence"
        subtitle="Notícias e vídeos do canal oficial, coletados automaticamente."
      />

      <div className="tabs" role="tablist" aria-label="Fonte do radar">
        {TABS.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={tab === name}
            className={`tab${tab === name ? ' is-active' : ''}`}
            onClick={() => setTab(name)}
          >
            {name}
          </button>
        ))}
      </div>

      {tab === 'Notícias' && <NewsTab />}
      {tab === 'Vídeos' && <VideosTab />}
    </>
  );
}

const NEWS_PAGE_SIZE = 30;

function NewsTab() {
  const { user, can } = useAuth();
  const [relevance, setRelevance] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [archived, setArchived] = useState(false);
  const [page, setPage] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const query = queryString({
    relevance: relevance || undefined,
    sourceType: sourceType || undefined,
    archived: archived ? 'true' : undefined,
    limit: String(NEWS_PAGE_SIZE),
    offset: String(page * NEWS_PAGE_SIZE),
  });
  const news = useAsync(
    () => api.get<{ items: NewsItem[]; total: number }>(`/news${query}`),
    [query],
  );
  const total = news.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / NEWS_PAGE_SIZE));

  function changeRelevance(value: string) {
    setRelevance(value);
    setPage(0);
  }

  function changeSourceType(value: string) {
    setSourceType(value);
    setPage(0);
  }

  function changeArchived(value: boolean) {
    setArchived(value);
    setPage(0);
  }

  async function refresh() {
    setRefreshing(true);
    setNotice(null);
    try {
      const result = await api.post<{ inserted: number; failed: string[] }>('/news/refresh');
      setNotice(
        result.inserted > 0
          ? `${result.inserted} notícia(s) nova(s).`
          : 'Nenhuma novidade desde a última coleta.',
      );
      if (result.failed.length > 0) {
        setNotice((n) => `${n ?? ''} Falhas: ${result.failed.join('; ')}`);
      }
      news.reload();
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Falha ao atualizar o radar.');
    } finally {
      setRefreshing(false);
    }
  }

  async function markRead(item: NewsItem) {
    if (!user || item.readBy.includes(user.id)) return;
    await api.post(`/news/${item.id}/read`);
    news.reload();
  }

  async function setRelevanceFor(item: NewsItem, value: NewsRelevance) {
    await api.patch(`/news/${item.id}`, { relevance: value });
    news.reload();
  }

  async function archive(item: NewsItem) {
    await api.patch(`/news/${item.id}`, { archived: !item.archived });
    news.reload();
  }

  async function togglePosted(item: NewsItem) {
    await api.patch(`/news/${item.id}`, { posted: !item.posted });
    news.reload();
  }

  return (
    <>
      {can('gerente') && (
        <div className="toolbar card">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => void refresh()}
            disabled={refreshing}
          >
            {refreshing ? 'Buscando…' : 'Atualizar agora'}
          </button>
          <select
            className="select"
            value={relevance}
            onChange={(e) => changeRelevance(e.target.value)}
            aria-label="Filtrar por relevância"
          >
            <option value="">Todas as relevâncias</option>
            {(['alta', 'media', 'baixa'] as NewsRelevance[]).map((r) => (
              <option key={r} value={r}>
                {RELEVANCE_LABEL[r]}
              </option>
            ))}
          </select>
          <select
            className="select"
            value={sourceType}
            onChange={(e) => changeSourceType(e.target.value)}
            aria-label="Filtrar por fonte"
          >
            <option value="">Todas as fontes</option>
            {(['tier1', 'geral', 'reddit'] as NewsSourceType[]).map((s) => (
              <option key={s} value={s}>
                {SOURCE_TYPE_LABEL[s]}
              </option>
            ))}
          </select>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={archived}
              onChange={(e) => changeArchived(e.target.checked)}
            />
            Mostrar arquivadas
          </label>
        </div>
      )}

      {notice && <p className="callout">{notice}</p>}

      {news.loading && <Loading label="Carregando radar…" />}
      {news.error && <ErrorState message={news.error} onRetry={news.reload} />}

      {news.data && news.data.items.length === 0 && (
        <EmptyState
          title="Nada no radar ainda."
          hint={
            can('gerente')
              ? 'Use "Atualizar agora" para fazer a primeira coleta.'
              : 'A coleta automática roda periodicamente.'
          }
        />
      )}

      <div className="news-list">
        {(news.data?.items ?? []).map((item) => {
          const read = user ? item.readBy.includes(user.id) : false;
          return (
            <article
              className={`card news-item${read ? ' is-read' : ''}${item.posted ? ' is-posted' : ''}`}
              key={item.id}
            >
              <div className="news-item__head">
                <a
                  className="text-body-strong link"
                  href={item.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={() => void markRead(item)}
                >
                  {item.title}
                </a>
                <div className="news-item__badges">
                  {item.posted && <span className="badge badge-status-success">Postamos</span>}
                  <span className="badge badge-status-neutral">{SOURCE_TYPE_LABEL[item.sourceType]}</span>
                  {item.relevance && (
                    <span className={`badge ${RELEVANCE_CLASS[item.relevance]}`}>
                      {RELEVANCE_LABEL[item.relevance]}
                    </span>
                  )}
                </div>
              </div>

              {item.summary && <p className="text-micro text-muted">{item.summary}</p>}

              <div className="news-item__meta">
                <span className="text-micro text-muted">
                  {item.sourceName} · {formatDateTime(item.publishedAt ?? item.fetchedAt)}
                  {item.posted && item.postedAt && <> · postado em {formatDateTime(item.postedAt)}</>}
                </span>

                {can('gerente') && (
                  <div className="news-item__actions">
                    <select
                      className="select select--sm"
                      value={item.relevance ?? ''}
                      onChange={(e) => void setRelevanceFor(item, e.target.value as NewsRelevance)}
                      aria-label={`Relevância de ${item.title}`}
                    >
                      <option value="">Sem classificação</option>
                      {(['alta', 'media', 'baixa'] as NewsRelevance[]).map((r) => (
                        <option key={r} value={r}>
                          {RELEVANCE_LABEL[r]}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className={`btn btn-sm ${item.posted ? 'btn-secondary' : 'btn-ghost'}`}
                      onClick={() => void togglePosted(item)}
                    >
                      {item.posted ? 'Postamos ✓' : 'Marcar como postado'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => void archive(item)}
                    >
                      {item.archived ? 'Desarquivar' : 'Arquivar'}
                    </button>
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {total > NEWS_PAGE_SIZE && (
        <div className="pagination">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            ← Anterior
          </button>
          <span className="text-micro text-muted">
            Página {page + 1} de {totalPages} · {formatNumber(total)} notícia(s)
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page + 1 >= totalPages}
          >
            Próxima →
          </button>
        </div>
      )}
    </>
  );
}

const VIDEO_SORT_OPTIONS = [
  { value: 'recent', label: 'Mais recentes primeiro' },
  { value: 'oldest', label: 'Mais antigos primeiro' },
  { value: 'views', label: 'Mais visualizações' },
] as const;
type VideoSort = (typeof VIDEO_SORT_OPTIONS)[number]['value'];

function sortVideos(items: VideoItem[], sort: VideoSort): VideoItem[] {
  const dateOf = (v: VideoItem) => new Date(v.publishedAt ?? v.fetchedAt).getTime();
  const sorted = [...items];
  if (sort === 'recent') sorted.sort((a, b) => dateOf(b) - dateOf(a));
  else if (sort === 'oldest') sorted.sort((a, b) => dateOf(a) - dateOf(b));
  else if (sort === 'views') sorted.sort((a, b) => (b.viewCount ?? -1) - (a.viewCount ?? -1));
  return sorted;
}

function VideosTab() {
  const { can } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [openVideo, setOpenVideo] = useState<VideoItem | null>(null);
  const [sort, setSort] = useState<VideoSort>('recent');
  const videos = useAsync(() => api.get<VideoItem[]>('/videos'), []);

  const sortedVideos = videos.data ? sortVideos(videos.data, sort) : [];
  const lastUpdated = videos.data?.length
    ? videos.data.reduce((max, v) => (v.fetchedAt > max ? v.fetchedAt : max), videos.data[0].fetchedAt)
    : null;

  async function refresh() {
    setRefreshing(true);
    setNotice(null);
    try {
      const result = await api.post<{ configured: boolean; inserted: number; failed: string[] }>(
        '/videos/refresh',
      );
      if (!result.configured) {
        setNotice(
          integrationNotConfiguredNotice('Radar de vídeo', 'YOUTUBE_API_KEY', can('superadmin')),
        );
      } else {
        setNotice(
          result.inserted > 0
            ? `${result.inserted} vídeo(s) novo(s).`
            : 'Nenhuma novidade desde a última coleta.',
        );
        if (result.failed.length > 0) {
          setNotice((n) => `${n ?? ''} Falhas: ${result.failed.join('; ')}`);
        }
      }
      videos.reload();
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Falha ao atualizar o radar de vídeo.');
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <>
      <div className="toolbar card">
        <select
          className="select"
          value={sort}
          onChange={(e) => setSort(e.target.value as VideoSort)}
          aria-label="Ordenar vídeos"
        >
          {VIDEO_SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {lastUpdated && (
          <span className="text-micro text-muted">
            Última atualização: {formatDateTime(lastUpdated)}
          </span>
        )}

        {can('gerente') && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => void refresh()}
            disabled={refreshing}
          >
            {refreshing ? 'Buscando…' : 'Atualizar agora'}
          </button>
        )}
      </div>

      {notice && <p className="callout">{notice}</p>}

      {videos.loading && <Loading label="Carregando vídeos…" />}
      {videos.error && <ErrorState message={videos.error} onRetry={videos.reload} />}

      {videos.data && videos.data.length === 0 && (
        <EmptyState
          title="Nenhum vídeo coletado ainda."
          hint={
            can('superadmin')
              ? 'Use "Atualizar agora" — exige YOUTUBE_API_KEY configurado no servidor.'
              : can('gerente')
                ? 'Fale com o Naldo para ativar essa integração.'
                : 'A coleta automática roda periodicamente, quando configurada.'
          }
        />
      )}

      <div className="video-grid">
        {sortedVideos.map((video) => (
          <button
            type="button"
            className="card video-card"
            key={video.id}
            onClick={() => setOpenVideo(video)}
          >
            {video.thumbnailUrl && (
              <img className="video-card__thumb" src={video.thumbnailUrl} alt="" loading="lazy" />
            )}
            <div className="video-card__body">
              <span className="text-body-strong">{video.title}</span>
              <span className="text-micro text-muted">
                {video.publishedAt ? formatDateTime(video.publishedAt) : formatDateTime(video.fetchedAt)}
                {video.viewCount !== null && <> · {formatNumber(video.viewCount)} views</>}
              </span>
            </div>
          </button>
        ))}
      </div>

      {openVideo && <VideoDetailModal video={openVideo} onClose={() => setOpenVideo(null)} />}
    </>
  );
}
